import React, { useEffect, useRef, useState } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import {
    getSendAsset,
    getSendCurrency,
    getSendNetwork,
    getSendFee,
    getTwoFactorOptions,
    sendCrypto,
    setCabinetWalletSendModalStatus,
} from "../../../store/cabinet/action";

import warningIcon from "../../../theme/assets/svg/b_cabinet_receive_warning_icon.svg";
import confirmIcon from "../../../theme/assets/svg/b_cabinet_deposit_confirm_icon.svg";
import feeIcon from "../../../theme/assets/svg/b_cabinet_deposit_fee_icon.svg";

import CryptoIcon from "../../_UI/cryptoIcon";
import TwoFactorCodeInput from "../../_UI/twoFactorCodeInput";

const CODE_LENGTH = 6;
const SEND_2FA_SCOPE = "transaction";

const SEND_STEPS = {
    FORM: 1,
    CONFIRM: 2,
};

const getAssetTypeLabel = (balance, selectedNetwork) => {
    const assetType = balance?.asset?.type;
    const networkCode = selectedNetwork?.code;

    if (assetType && assetType !== "native") {
        return assetType
            .toUpperCase()
            .replace("TRC20", "TRC-20")
            .replace("ERC20", "ERC-20")
            .replace("BEP20", "BEP-20");
    }

    if (networkCode) {
        return networkCode.toUpperCase();
    }

    return "";
};

const formatNumber = (value, maxDigits = 6) => {
    if (value === null || value === undefined || value === "") return "—";

    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) return "—";

    return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: maxDigits,
    }).format(numberValue);
};

const getFeeUsdValue = (fee) => {
    return fee?.fee_usd || null;
};

const getTwoFactorScopes = (options) => {
    if (Array.isArray(options)) return options;
    if (Array.isArray(options?.scopes)) return options.scopes;

    return [];
};

const isTwoFactorScopeEnabled = (options, scope) => {
    if (!options?.enabled) return false;

    return getTwoFactorScopes(options).includes(scope);
};

const shortenAddress = (address) => {
    if (!address) return "—";

    const normalizedAddress = String(address);

    if (normalizedAddress.length <= 18) return normalizedAddress;

    return `${normalizedAddress.slice(0, 10)}...${normalizedAddress.slice(-5)}`;
};

const getSenderAddress = (assetData) => {
    return assetData?.address || "";
};

const CabinetWalletSendModal = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const dropdownRef = useRef(null);

    const modalStatus = useSelector(store => store.cabinet.cabinetWalletSendModalStatus);
    const sendCurrency = useSelector(store => store.cabinet.sendCurrency);
    const sendNetwork = useSelector(store => store.cabinet.sendNetwork);
    const sendNetworkCurrencyId = useSelector(store => store.cabinet.sendNetworkCurrencyId);
    const sendAsset = useSelector(store => store.cabinet.sendAsset);
    const sendFee = useSelector(store => store.cabinet.sendFee);

    const sendCryptoStarted = useSelector(store => store.cabinet.sendCryptoStarted) || false;
    const sendCryptoErr = useSelector(store => store.cabinet.sendCryptoErr) || null;

    const twoFactorOptions = useSelector(store => store.cabinet.twoFactorOptions);

    const [step, setStep] = useState(SEND_STEPS.FORM);
    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const [selectedNetwork, setSelectedNetwork] = useState(null);

    const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
    const [isNetworkDropdownOpen, setIsNetworkDropdownOpen] = useState(false);

    const [recipientAddress, setRecipientAddress] = useState("");
    const [amount, setAmount] = useState("");
    const [verificationCode, setVerificationCode] = useState("");

    const hasCurrencyDropdown = sendCurrency?.length > 1;

    const selectedBalance = sendAsset?.balances?.find((balance) => {
        return (
            balance?.asset?.currency?.id === selectedCurrency?.id &&
            balance?.asset?.network?.id === selectedNetwork?.id
        );
    });

    const selectedAssetType = getAssetTypeLabel(selectedBalance, selectedNetwork);
    const selectedAssetIconType = selectedBalance?.asset?.type || selectedNetwork?.type || "";
    const selectedAssetNetworkCode =
        selectedBalance?.asset?.network?.code ||
        selectedNetwork?.code ||
        selectedNetwork?.native_symbol ||
        "";

    const selectedCurrencyCode = selectedCurrency?.code || "";

    const isSendNetworkActual = sendNetworkCurrencyId === selectedCurrency?.id;
    const actualSendNetwork = isSendNetworkActual ? sendNetwork : false;
    const hasNetworkDropdown = actualSendNetwork?.length > 1;

    const availableBalance = selectedBalance?.amount_decimal || selectedBalance?.amount || "0";
    const availableBalanceNumber = Number(availableBalance) || 0;

    const amountNumber = Number(amount) || 0;

    const feeDecimal = sendFee?.fee_decimal || "";
    const feeNativeSymbol = sendFee?.native_symbol || selectedNetwork?.native_symbol || "";
    const feeUsdValue = getFeeUsdValue(sendFee);

    const hasFeeUsdValue =
        feeUsdValue !== null &&
        feeUsdValue !== undefined &&
        feeUsdValue !== "";

    const networkFeeLabel = feeDecimal
        ? `${formatNumber(feeDecimal, 8)} ${feeNativeSymbol}${hasFeeUsdValue ? ` (≈ $${formatNumber(feeUsdValue, 2)})` : ""}`
        : "—";

    const estimatedArrival =
        sendAsset?.estimated_arrival ||
        sendAsset?.arrival_time ||
        "~1–3 minutes";

    const selectedNetworkLabel = selectedAssetType || selectedNetwork?.code?.toUpperCase() || selectedNetwork?.name || "—";
    const senderAddress = getSenderAddress(sendAsset);

    const amountLabel = amountNumber > 0
        ? `${formatNumber(amountNumber, selectedCurrency?.precision || 6)} ${selectedCurrencyCode}`
        : `0 ${selectedCurrencyCode}`;

    const isSend2faRequired = isTwoFactorScopeEnabled(twoFactorOptions, SEND_2FA_SCOPE);

    const isFormConfirmDisabled =
        !selectedCurrency?.id ||
        !selectedNetwork?.id ||
        !recipientAddress.trim() ||
        amountNumber <= 0 ||
        amountNumber > availableBalanceNumber;

    const isFinalConfirmDisabled =
        sendCryptoStarted ||
        (isSend2faRequired && verificationCode.length !== CODE_LENGTH);

    const closeModal = () => {
        if (sendCryptoStarted) return;

        dispatch(setCabinetWalletSendModalStatus(false));
        setIsCurrencyDropdownOpen(false);
        setIsNetworkDropdownOpen(false);
    };

    const currencySelectHandler = (currency) => {
        setSelectedCurrency(currency);
        setSelectedNetwork(null);
        setAmount("");
        setIsCurrencyDropdownOpen(false);
        setIsNetworkDropdownOpen(false);
    };

    const networkSelectHandler = (network) => {
        setSelectedNetwork(network);
        setAmount("");
        setIsNetworkDropdownOpen(false);
    };

    const currencyDropdownToggleHandler = () => {
        if (!hasCurrencyDropdown) return;

        setIsCurrencyDropdownOpen(prev => !prev);
        setIsNetworkDropdownOpen(false);
    };

    const networkDropdownToggleHandler = () => {
        if (!hasNetworkDropdown) return;

        setIsNetworkDropdownOpen(prev => !prev);
        setIsCurrencyDropdownOpen(false);
    };

    const amountChangeHandler = (event) => {
        const value = event.target.value.replace(",", ".");

        if (/^\d*\.?\d*$/.test(value)) {
            setAmount(value);
        }
    };

    const setMaxAmountHandler = () => {
        setAmount(String(availableBalance || "0"));
    };

    const continueHandler = () => {
        if (isFormConfirmDisabled) return;

        setVerificationCode("");
        setIsCurrencyDropdownOpen(false);
        setIsNetworkDropdownOpen(false);
        setStep(SEND_STEPS.CONFIRM);
    };

    const backToFormHandler = () => {
        if (sendCryptoStarted) return;

        setVerificationCode("");
        setStep(SEND_STEPS.FORM);
    };

    const successHandler = () => {
        toast(t("Транзакция успешно отправлена"));
        closeModal();
    };

    const confirmSendHandler = () => {
        if (isFinalConfirmDisabled) return;

        const payload = {
            currency_id: selectedCurrency.id,
            network_id: selectedNetwork.id,
            to: recipientAddress.trim(),
            amount,
        };

        if (isSend2faRequired) {
            payload.code = verificationCode;
        }

        dispatch(sendCrypto(payload, successHandler));
    };

    useEffect(() => {
        if (!modalStatus) return;

        dispatch(getSendCurrency());
        dispatch(getTwoFactorOptions());
    }, [modalStatus, dispatch]);

    useEffect(() => {
        if (!modalStatus) {
            setStep(SEND_STEPS.FORM);
            setSelectedCurrency(null);
            setSelectedNetwork(null);
            setRecipientAddress("");
            setAmount("");
            setVerificationCode("");
            setIsCurrencyDropdownOpen(false);
            setIsNetworkDropdownOpen(false);

            return;
        }

        if (!selectedCurrency && sendCurrency?.length) {
            setSelectedCurrency(sendCurrency[0]);
        }
    }, [modalStatus, sendCurrency, selectedCurrency]);

    useEffect(() => {
        if (!modalStatus) return;
        if (!selectedCurrency?.id) return;

        dispatch(getSendNetwork(selectedCurrency.id));
    }, [dispatch, modalStatus, selectedCurrency]);

    useEffect(() => {
        if (!modalStatus) return;
        if (!selectedCurrency?.id) {
            setSelectedNetwork(null);
            return;
        }

        if (sendNetworkCurrencyId !== selectedCurrency.id) return;

        if (actualSendNetwork?.length) {
            setSelectedNetwork((prevState) => {
                const isSelectedNetworkAvailable = actualSendNetwork.some((network) => {
                    return network?.id === prevState?.id;
                });

                return isSelectedNetworkAvailable ? prevState : actualSendNetwork[0];
            });

            return;
        }

        setSelectedNetwork(null);
    }, [modalStatus, actualSendNetwork, sendNetworkCurrencyId, selectedCurrency]);

    useEffect(() => {
        if (!modalStatus) return;
        if (!selectedCurrency?.id || !selectedNetwork?.id) return;

        dispatch(getSendFee(selectedCurrency.id, selectedNetwork.id));
        dispatch(getSendAsset(selectedCurrency.id, selectedNetwork.id));
    }, [dispatch, modalStatus, selectedCurrency, selectedNetwork]);

    useEffect(() => {
        const clickOutsideHandler = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsCurrencyDropdownOpen(false);
                setIsNetworkDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", clickOutsideHandler);

        return () => {
            document.removeEventListener("mousedown", clickOutsideHandler);
        };
    }, []);

    return (
        <Popup
            open={!!modalStatus}
            modal
            nested
            closeOnDocumentClick={!sendCryptoStarted}
            onClose={closeModal}
            className="cabinetWalletSendPopup"
        >
            <div className={Styles.modalContainer}>
                <div
                    className={`
                        ${Styles.modalCloseBtn}
                        ${sendCryptoStarted ? Styles.modalCloseBtnDisabled : ""}
                    `}
                    onClick={closeModal}
                >
                    <div />
                    <div />
                </div>

                <div className={Styles.modalContentWrap}>
                    {step === SEND_STEPS.FORM && (
                        <>
                            <div className={Styles.titleRow}>
                                <div className={Styles.titleRowImgWrap}>
                                    <svg
                                        width="26"
                                        height="26"
                                        viewBox="0 0 26 26"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M13 21V5"
                                            stroke="currentColor"
                                            strokeWidth="2.4"
                                            strokeLinecap="round"
                                        />
                                        <path
                                            d="M7 11L13 5L19 11"
                                            stroke="currentColor"
                                            strokeWidth="2.4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>

                                <div className={Styles.titleRowText}>
                                    <div className={Styles.title}>
                                        {t("Отправить криптовалюту")}
                                    </div>

                                    <div className={Styles.note}>
                                        {t("Переведите цифровые активы безопасно")}
                                    </div>
                                </div>
                            </div>

                            <div className={Styles.sendBodyScroll}>
                                <div
                                    className={Styles.formWrap}
                                    ref={dropdownRef}
                                >
                                    <div className={Styles.formItem}>
                                        <div className={Styles.formLabel}>
                                            {t("Актив")}
                                        </div>

                                        <div className={Styles.dropdownWrap}>
                                            <button
                                                type="button"
                                                className={`
                                                    ${Styles.dropdownBtn}
                                                    ${isCurrencyDropdownOpen ? Styles.dropdownBtnActive : ""}
                                                    ${!hasCurrencyDropdown ? Styles.dropdownBtnSingle : ""}
                                                `}
                                                onClick={currencyDropdownToggleHandler}
                                                disabled={!sendCurrency?.length}
                                            >
                                                <div className={Styles.dropdownAsset}>
                                                    <CryptoIcon
                                                        code={selectedCurrencyCode}
                                                        networkCode={selectedAssetNetworkCode}
                                                        assetType={selectedAssetIconType}
                                                        className={Styles.currencyIcon}
                                                    />

                                                    <div className={Styles.dropdownAssetText}>
                                                        <div className={Styles.dropdownAssetName}>
                                                            {selectedCurrencyCode || t("Выберите актив")}
                                                        </div>

                                                        <div className={Styles.dropdownAssetCode}>
                                                            {selectedAssetType || selectedCurrency?.name || ""}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className={Styles.dropdownBalance}>
                                                    {t("Баланс")}: {formatNumber(availableBalance, selectedCurrency?.precision || 6)} {selectedCurrencyCode}
                                                </div>

                                                {hasCurrencyDropdown && (
                                                    <div className={Styles.dropdownArrow} />
                                                )}
                                            </button>

                                            {isCurrencyDropdownOpen && hasCurrencyDropdown && (
                                                <div className={Styles.dropdownList}>
                                                    <div className={Styles.dropdownListScroll}>
                                                        {sendCurrency?.map((currency) => {
                                                            const isActive = selectedCurrency?.id === currency.id;

                                                            return (
                                                                <button
                                                                    type="button"
                                                                    className={`
                                                                        ${Styles.dropdownListItem}
                                                                        ${isActive ? Styles.dropdownListItemActive : ""}
                                                                    `}
                                                                    key={currency.id}
                                                                    onClick={() => currencySelectHandler(currency)}
                                                                >
                                                                    <CryptoIcon
                                                                        code={currency.code}
                                                                        className={Styles.currencyIcon}
                                                                    />

                                                                    <div className={Styles.dropdownAssetText}>
                                                                        <div className={Styles.dropdownAssetName}>
                                                                            {currency.code}
                                                                        </div>

                                                                        <div className={Styles.dropdownAssetCode}>
                                                                            {currency.name}
                                                                        </div>
                                                                    </div>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className={Styles.formItem}>
                                        <div className={Styles.formLabel}>
                                            {t("Сеть")}
                                        </div>

                                        <div className={Styles.dropdownWrap}>
                                            <button
                                                type="button"
                                                className={`
                                                    ${Styles.dropdownBtn}
                                                    ${isNetworkDropdownOpen ? Styles.dropdownBtnActive : ""}
                                                    ${!hasNetworkDropdown ? Styles.dropdownBtnSingle : ""}
                                                `}
                                                onClick={networkDropdownToggleHandler}
                                                disabled={!actualSendNetwork?.length}
                                            >
                                                <div className={Styles.dropdownAsset}>
                                                    <CryptoIcon
                                                        code={selectedCurrencyCode}
                                                        networkCode={selectedNetwork?.code || selectedNetwork?.native_symbol}
                                                        assetType={selectedNetwork?.type || selectedAssetIconType}
                                                        className={Styles.currencyIcon}
                                                    />

                                                    <div className={Styles.dropdownAssetText}>
                                                        <div className={Styles.dropdownAssetName}>
                                                            {selectedNetwork?.name || t("Выберите сеть")}
                                                        </div>

                                                        <div className={Styles.dropdownAssetCode}>
                                                            {selectedNetwork?.code?.toUpperCase() || ""}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className={Styles.dropdownBalance}>
                                                    {selectedNetwork?.type?.toUpperCase() || ""}
                                                </div>

                                                {hasNetworkDropdown && (
                                                    <div className={Styles.dropdownArrow} />
                                                )}
                                            </button>

                                            {isNetworkDropdownOpen && hasNetworkDropdown && (
                                                <div className={Styles.dropdownList}>
                                                    <div className={Styles.dropdownListScroll}>
                                                        {actualSendNetwork?.map((network) => {
                                                            const isActive = selectedNetwork?.id === network.id;

                                                            return (
                                                                <button
                                                                    type="button"
                                                                    className={`
                                                                        ${Styles.dropdownListItem}
                                                                        ${isActive ? Styles.dropdownListItemActive : ""}
                                                                    `}
                                                                    key={network.id}
                                                                    onClick={() => networkSelectHandler(network)}
                                                                >
                                                                    <CryptoIcon
                                                                        code={selectedCurrencyCode}
                                                                        networkCode={network?.code || network?.native_symbol}
                                                                        assetType={network?.type}
                                                                        className={Styles.currencyIcon}
                                                                    />

                                                                    <div className={Styles.dropdownAssetText}>
                                                                        <div className={Styles.dropdownAssetName}>
                                                                            {network.name}
                                                                        </div>

                                                                        <div className={Styles.dropdownAssetCode}>
                                                                            {network.code?.toUpperCase()}
                                                                        </div>
                                                                    </div>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className={Styles.fieldWrap}>
                                    <div className={Styles.formLabel}>
                                        {t("Адрес получателя")}
                                    </div>

                                    <div className={Styles.addressInputWrap}>
                                        <input
                                            type="text"
                                            value={recipientAddress}
                                            onChange={(event) => setRecipientAddress(event.target.value)}
                                            placeholder={t("Введите адрес получателя")}
                                        />

                                        <button
                                            type="button"
                                            className={Styles.scanBtn}
                                            aria-label={t("Сканировать адрес")}
                                        >
                                            <span className={Styles.scanIcon}>
                                                <span />
                                                <span />
                                                <span />
                                                <span />
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                <div className={Styles.fieldWrap}>
                                    <div className={Styles.amountTopRow}>
                                        <div className={Styles.formLabel}>
                                            {t("Сумма")}
                                        </div>

                                        <div className={Styles.availableText}>
                                            {t("Доступно")}: {formatNumber(availableBalance, selectedCurrency?.precision || 6)} {selectedCurrencyCode}
                                        </div>
                                    </div>

                                    <div className={Styles.amountInputWrap}>
                                        <input
                                            type="text"
                                            value={amount}
                                            onChange={amountChangeHandler}
                                            placeholder="0"
                                            inputMode="decimal"
                                        />

                                        <div className={Styles.amountCurrency}>
                                            {selectedCurrencyCode}
                                        </div>

                                        <button
                                            type="button"
                                            className={Styles.maxBtn}
                                            onClick={setMaxAmountHandler}
                                            disabled={!availableBalanceNumber}
                                        >
                                            {t("MAX")}
                                        </button>
                                    </div>
                                </div>

                                <div className={Styles.warningRow}>
                                    <div className={Styles.warningRowImgWrap}>
                                        <img src={warningIcon} alt="" />
                                    </div>

                                    <div className={Styles.warningRowText}>
                                        <div className={Styles.mainText}>
                                            {t("Проверьте сеть и адрес получателя.")}
                                        </div>

                                        <div className={Styles.subText}>
                                            {t("Транзакции в блокчейне необратимы.")}
                                        </div>
                                    </div>
                                </div>

                                <div className={Styles.actionsRow}>
                                    <button
                                        type="button"
                                        className={Styles.confirmBtn}
                                        onClick={continueHandler}
                                        disabled={isFormConfirmDisabled}
                                    >
                                        {t("Подтвердить и отправить")}
                                    </button>

                                    <button
                                        type="button"
                                        className={Styles.cancelBtn}
                                        onClick={closeModal}
                                    >
                                        {t("Отмена")}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    {step === SEND_STEPS.CONFIRM && (
                        <>
                            <div className={Styles.titleRow}>
                                <div className={`${Styles.titleRowImgWrap} ${Styles.titleConfirmIcon}`}>
                                    <img src={confirmIcon} alt="" />
                                </div>

                                <div className={Styles.titleRowText}>
                                    <div className={Styles.title}>
                                        {t("Подтверждение перевода")}
                                    </div>

                                    <div className={Styles.note}>
                                        {isSend2faRequired
                                            ? t("Введите 6-значный код из приложения-аутентификатора")
                                            : t("Проверьте детали перевода перед отправкой")}
                                    </div>
                                </div>
                            </div>

                            <div className={Styles.sendBodyScroll}>
                                <div className={Styles.confirmBox}>
                                    <div className={Styles.confirmRow}>
                                        <span>{t("Итого")}</span>
                                        <strong>{amountLabel}</strong>
                                    </div>

                                    <div className={Styles.confirmRow}>
                                        <span>{t("Актив")}</span>
                                        <strong className={Styles.confirmValueWithIcon}>
                                            <CryptoIcon
                                                code={selectedCurrencyCode}
                                                networkCode={selectedAssetNetworkCode}
                                                assetType={selectedAssetIconType}
                                                className={Styles.confirmCryptoIcon}
                                            />
                                            {selectedCurrencyCode}
                                        </strong>
                                    </div>

                                    <div className={Styles.confirmRow}>
                                        <span>{t("Сеть")}</span>
                                        <strong className={Styles.confirmValueWithIcon}>
                                            <CryptoIcon
                                                code={selectedCurrencyCode}
                                                networkCode={selectedNetwork?.code || selectedNetwork?.native_symbol}
                                                assetType={selectedNetwork?.type || selectedAssetIconType}
                                                className={Styles.confirmCryptoIcon}
                                            />
                                            {selectedNetworkLabel}
                                        </strong>
                                    </div>

                                    <div className={Styles.confirmRow}>
                                        <span>{t("Откуда")}</span>
                                        <strong>{shortenAddress(senderAddress)}</strong>
                                    </div>

                                    <div className={Styles.confirmRow}>
                                        <span>{t("Куда")}</span>
                                        <strong>{shortenAddress(recipientAddress)}</strong>
                                    </div>

                                    <div className={Styles.confirmRow}>
                                        <span>{t("Сумма")}</span>
                                        <strong>{amountLabel}</strong>
                                    </div>

                                    <div className={Styles.confirmRow}>
                                        <span>{t("Скорость")}</span>
                                        <strong>{estimatedArrival}</strong>
                                    </div>

                                    <div className={Styles.networkFeeBox}>
                                        <div className={Styles.networkFeeIcon}>
                                            <img src={feeIcon} alt="" />
                                        </div>

                                        <div className={Styles.networkFeeText}>
                                            <div className={Styles.networkFeeTitle}>
                                                {t("Комиссия сети")}
                                            </div>

                                            <div className={Styles.networkFeeValue}>
                                                {networkFeeLabel}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {isSend2faRequired && (
                                    <TwoFactorCodeInput
                                        value={verificationCode}
                                        onChange={setVerificationCode}
                                        formErr={sendCryptoErr}
                                        errorFieldName="code"
                                        isLoading={sendCryptoStarted}
                                        label="Код аутентификации"
                                        hint="Откройте Google Authenticator и введите код, который сейчас отображается для Kairo."
                                    />
                                )}

                                <div className={Styles.warningRow}>
                                    <div className={Styles.warningRowImgWrap}>
                                        <img src={warningIcon} alt="" />
                                    </div>

                                    <div className={Styles.warningRowText}>
                                        <div className={Styles.mainText}>
                                            {t("Проверьте все данные перевода.")}
                                        </div>

                                        <div className={Styles.subText}>
                                            {t("Криптовалютные транзакции нельзя отменить после подтверждения.")}
                                        </div>
                                    </div>
                                </div>

                                <div className={Styles.actionsRow}>
                                    <button
                                        type="button"
                                        className={Styles.cancelBtn}
                                        onClick={backToFormHandler}
                                        disabled={sendCryptoStarted}
                                    >
                                        {t("Отмена")}
                                    </button>

                                    <button
                                        type="button"
                                        className={Styles.confirmBtn}
                                        onClick={confirmSendHandler}
                                        disabled={isFinalConfirmDisabled}
                                    >
                                        {sendCryptoStarted ? t("Отправка") : t("Проверить и отправить")}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Popup>
    );
};

export default CabinetWalletSendModal;
